import React from 'react';
import { AlertTriangle, Eye, Link2, Plus, RefreshCw, ShieldCheck, X } from 'lucide-react';
import { contactsApi } from '../../services/contactsApi';
import type { GoogleContactSyncRun, GoogleDiagnosticResult, GoogleDiscoveredAccount, GoogleDuplicateRiskDetail, GoogleDuplicateRiskItem } from '../../services/customerApi';

const fmtDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const fmtToken = (value?: string | null) => {
  const token = String(value || '').trim();
  if (!token) return '—';
  return token.length > 18 ? `${token.slice(0, 18)}…` : token;
};

const surfaceLabel = (surface?: 'CONTACT' | 'OTHER_CONTACT') =>
  surface === 'OTHER_CONTACT' ? 'Other Contacts' : 'Saved Contacts';

const surfaceBadgeTone = (surface?: 'CONTACT' | 'OTHER_CONTACT') =>
  surface === 'OTHER_CONTACT' ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700';

type GoogleContactsTabState = {
  accounts: GoogleDiscoveredAccount[];
  runs: GoogleContactSyncRun[];
  risks: GoogleDuplicateRiskItem[];
  ignoredRisks: GoogleDuplicateRiskItem[];
  customersById: Map<string, any>;
};

export const GoogleContactsTab: React.FC = () => {
  const [state, setState] = React.useState<GoogleContactsTabState>({
    accounts: [],
    runs: [],
    risks: [],
    ignoredRisks: [],
    customersById: new Map(),
  });
  const [loading, setLoading] = React.useState(true);
  const [busyKey, setBusyKey] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState<string | null>(null);
  const [selectedRiskDetail, setSelectedRiskDetail] = React.useState<GoogleDuplicateRiskDetail | null>(null);
  const [selectedCandidate, setSelectedCandidate] = React.useState<any | null>(null);
  const [linkTarget, setLinkTarget] = React.useState<{ sourceId: string; customer: any } | null>(null);
  const [ignoreReason, setIgnoreReason] = React.useState('');
  const [diagnosticQuery, setDiagnosticQuery] = React.useState('');
  const [diagnosticResults, setDiagnosticResults] = React.useState<GoogleDiagnosticResult[]>([]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [accounts, runs, duplicateData] = await Promise.all([
        contactsApi.getGoogleAccounts(),
        contactsApi.getGoogleSyncRuns({ limit: 20 }),
        contactsApi.getGoogleDuplicateRisks({ limit: 80, includeIgnored: true }),
      ]);
      const allRisks = duplicateData.risks || [];
      const activeRisks = allRisks.filter(risk => !risk.ignoredAt);
      const ignoredRisks = allRisks.filter(risk => !!risk.ignoredAt);
      setState({
        accounts,
        runs,
        risks: activeRisks,
        ignoredRisks,
        customersById: new Map((duplicateData.customers || []).map((customer: any) => [customer.id, customer])),
      });
      setStatus(null);
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Failed to load Google Contacts health.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const hasActiveRuns = state.runs.some(run => run.status === 'QUEUED' || run.status === 'RUNNING');

  React.useEffect(() => {
    if (!hasActiveRuns) return;
    const timer = window.setInterval(() => {
      void load();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [hasActiveRuns, load]);

  const runAction = async (label: string, action: () => Promise<unknown>) => {
    try {
      setStatus(null);
      await action();
      setStatus(label);
      await load();
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Operation failed');
    } finally {
      setBusyKey(null);
    }
  };

  const openRiskDetail = async (sourceId: string) => {
    setBusyKey(`detail-${sourceId}`);
    try {
      const detail = await contactsApi.getGoogleDuplicateRiskDetail(sourceId);
      setSelectedRiskDetail(detail);
      setSelectedCandidate(null);
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Failed to load Google source detail');
    } finally {
      setBusyKey(null);
    }
  };

  const enabledAccounts = state.accounts.filter(account => account.syncEnabled && account.peopleReadEnabled);
  const activeRiskCount = state.risks.length;
  const ignoredRiskCount = state.ignoredRisks.length;
  const latestRunByAccount = new Map<string, GoogleContactSyncRun>();
  const latestRunByAccountSurface = new Map<string, GoogleContactSyncRun>();
  for (const run of state.runs) {
    if (!latestRunByAccount.has(run.accountKey)) {
      latestRunByAccount.set(run.accountKey, run);
    }
    const key = `${run.accountKey}:${run.sourceSurface}`;
    if (!latestRunByAccountSurface.has(key)) {
      latestRunByAccountSurface.set(key, run);
    }
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              <h3 className="text-base font-semibold text-slate-900">Google Contacts Health</h3>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              Read-only external source sync. MAOS `Customer` remains canonical.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setBusyKey('dry-run-all');
                void runAction('Saved-contact dry-run queued.', () => contactsApi.syncGoogleContacts({ dryRun: true, sourceSurface: 'CONTACT' }));
              }}
              disabled={busyKey !== null || enabledAccounts.length === 0}
              className="rounded-xl border border-amber-200 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-amber-700 disabled:opacity-50"
            >
              Dry Run Saved
            </button>
            <button
              type="button"
              onClick={() => {
                setBusyKey('sync-all');
                void runAction('Saved-contact sync queued.', () => contactsApi.syncGoogleContacts({ sourceSurface: 'CONTACT' }));
              }}
              disabled={busyKey !== null || enabledAccounts.length === 0}
              className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white disabled:opacity-50"
            >
              Sync Saved
            </button>
            <button
              type="button"
              onClick={() => {
                setBusyKey('dry-run-other-all');
                void runAction('Other-contact dry-run queued.', () => contactsApi.syncGoogleContacts({ dryRun: true, sourceSurface: 'OTHER_CONTACT' }));
              }}
              disabled={busyKey !== null || state.accounts.filter(account => account.syncEnabled && account.otherContactsReadEnabled).length === 0}
              className="rounded-xl border border-violet-200 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-violet-700 disabled:opacity-50"
            >
              Dry Run Other
            </button>
            <button
              type="button"
              onClick={() => {
                setBusyKey('sync-other-all');
                void runAction('Other-contact sync queued.', () => contactsApi.syncGoogleContacts({ sourceSurface: 'OTHER_CONTACT' }));
              }}
              disabled={busyKey !== null || state.accounts.filter(account => account.syncEnabled && account.otherContactsReadEnabled).length === 0}
              className="rounded-xl bg-violet-700 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white disabled:opacity-50"
            >
              Sync Other
            </button>
          </div>
        </div>
        {status ? (
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            {status}
          </div>
        ) : null}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-slate-900">Accounts</h4>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        <div className="space-y-3">
          {state.accounts.map(account => (
            <div key={account.accountKey} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              {(() => {
                const activeRun = latestRunByAccount.get(account.accountKey);
                const savedRun = latestRunByAccountSurface.get(`${account.accountKey}:CONTACT`);
                const otherRun = latestRunByAccountSurface.get(`${account.accountKey}:OTHER_CONTACT`);
                const isActive = activeRun?.status === 'QUEUED' || activeRun?.status === 'RUNNING';
                return (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-white px-2 py-0.5 text-xs font-bold text-slate-700">{account.accountKey}</span>
                    <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${account.peopleReadEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                      {account.peopleReadEnabled ? 'People Read Enabled' : 'People Read Missing'}
                    </span>
                    <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${account.otherContactsReadEnabled ? 'bg-violet-100 text-violet-700' : 'bg-slate-200 text-slate-600'}`}>
                      {account.otherContactsReadEnabled ? 'Other Contacts Read Enabled' : 'Other Contacts Read Missing'}
                    </span>
                    {account.peopleWriteEnabled ? (
                      <span className="rounded bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700">Write Scope Present</span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{account.mailboxEmail || 'Mailbox email unknown'}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Saved full: {fmtDateTime(account.syncState?.lastFullSyncAt)} · Saved incremental: {fmtDateTime(account.syncState?.lastIncrementalSyncAt)}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Other full: {fmtDateTime(account.syncState?.lastOtherContactsFullSyncAt)} · Other incremental: {fmtDateTime(account.syncState?.lastOtherContactsIncrementalSyncAt)}
                  </p>
                  {[savedRun, otherRun].filter(Boolean).map((run) => (
                    <div key={`${account.accountKey}-${run!.sourceSurface}`} className="mt-2">
                      <div className="flex items-center gap-2 text-[11px] text-slate-600">
                        <span className={`rounded px-2 py-0.5 font-bold ${surfaceBadgeTone(run!.sourceSurface)}`}>
                          {surfaceLabel(run!.sourceSurface)}
                        </span>
                        <span className={`rounded px-2 py-0.5 font-bold ${run!.status === 'RUNNING' ? 'bg-sky-100 text-sky-700' : run!.status === 'QUEUED' ? 'bg-amber-100 text-amber-700' : run!.status === 'SUCCESS' ? 'bg-emerald-100 text-emerald-700' : run!.status === 'PARTIAL' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
                          {run!.status}
                        </span>
                        <span>
                          scanned {run!.scannedCount} · linked {run!.linkedCustomerCount} · created {run!.createdCustomerCount} · duplicate risk {run!.duplicateRiskCount} · skipped unmatched {run!.skippedUnmatchedCount || 0}
                        </span>
                      </div>
                      {run!.status === 'QUEUED' || run!.status === 'RUNNING' ? (
                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
                          <div className="h-full w-1/2 animate-pulse rounded-full bg-sky-500" />
                        </div>
                      ) : null}
                      <p className="mt-1 text-[11px] text-slate-500">
                        Pages {run!.pagesProcessed || 0}
                        {run!.heartbeatAt ? ` · heartbeat ${fmtDateTime(run!.heartbeatAt)}` : ''}
                      </p>
                      {run!.status === 'QUEUED' || run!.status === 'RUNNING' ? (
                        <p className="mt-1 text-[11px] text-slate-500">
                          Next page token {fmtToken(run!.nextPageToken || run!.currentPageToken)}
                        </p>
                      ) : null}
                    </div>
                  ))}
                  {account.syncState?.lastError ? (
                    <p className="mt-1 text-xs text-rose-600">{account.syncState.lastError}</p>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
                    <input
                      type="checkbox"
                      checked={account.syncEnabled}
                      onChange={(e) => {
                        setBusyKey(`toggle-${account.accountKey}`);
                        void runAction(
                          `Sync ${e.target.checked ? 'enabled' : 'disabled'} for ${account.accountKey}.`,
                          () => contactsApi.updateGoogleAccount(account.accountKey, { syncEnabled: e.target.checked })
                        );
                      }}
                      disabled={busyKey !== null || isActive}
                    />
                    Sync Enabled
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setBusyKey(`dry-contact-${account.accountKey}`);
                      void runAction(`Saved-contact dry-run queued for ${account.accountKey}.`, () => contactsApi.syncGoogleContactsAccount(account.accountKey, { dryRun: true, sourceSurface: 'CONTACT' }));
                    }}
                    disabled={busyKey !== null || !account.peopleReadEnabled || isActive}
                    className="rounded-lg border border-amber-200 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-amber-700 disabled:opacity-50"
                  >
                    Dry Run Saved
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setBusyKey(`sync-contact-${account.accountKey}`);
                      void runAction(`Saved-contact sync queued for ${account.accountKey}.`, () => contactsApi.syncGoogleContactsAccount(account.accountKey, { sourceSurface: 'CONTACT' }));
                    }}
                    disabled={busyKey !== null || !account.peopleReadEnabled || !account.syncEnabled || isActive}
                    className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white disabled:opacity-50"
                  >
                    {savedRun?.status === 'QUEUED' || savedRun?.status === 'RUNNING' ? 'Saved Active' : 'Sync Saved'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setBusyKey(`dry-other-${account.accountKey}`);
                      void runAction(`Other-contacts dry-run queued for ${account.accountKey}.`, () => contactsApi.syncGoogleContactsAccount(account.accountKey, { dryRun: true, sourceSurface: 'OTHER_CONTACT' }));
                    }}
                    disabled={busyKey !== null || !account.otherContactsReadEnabled || isActive}
                    className="rounded-lg border border-violet-200 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-violet-700 disabled:opacity-50"
                  >
                    Dry Run Other
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setBusyKey(`sync-other-${account.accountKey}`);
                      void runAction(`Other-contacts sync queued for ${account.accountKey}.`, () => contactsApi.syncGoogleContactsAccount(account.accountKey, { sourceSurface: 'OTHER_CONTACT' }));
                    }}
                    disabled={busyKey !== null || !account.otherContactsReadEnabled || !account.syncEnabled || isActive}
                    className="rounded-lg bg-violet-700 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white disabled:opacity-50"
                  >
                    {otherRun?.status === 'QUEUED' || otherRun?.status === 'RUNNING' ? 'Other Active' : 'Sync Other'}
                  </button>
                </div>
              </div>
                );
              })()}
            </div>
          ))}
          {!loading && state.accounts.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
              No Google mailbox accounts discovered from the current env-based configuration.
            </div>
          ) : null}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="mb-4 flex items-center gap-2">
          <Eye className="h-4 w-4 text-slate-600" />
          <h4 className="text-sm font-semibold text-slate-900">Google Source Diagnostics</h4>
        </div>
        <div className="flex gap-2">
          <input
            value={diagnosticQuery}
            onChange={(e) => setDiagnosticQuery(e.target.value)}
            placeholder="Search email or name across saved + other contacts"
            className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700"
          />
          <button
            type="button"
            onClick={() => {
              setBusyKey('diagnostic-search');
              void runAction('Diagnostics refreshed.', async () => {
                const results = await contactsApi.searchGoogleDiagnostics(diagnosticQuery);
                setDiagnosticResults(results);
              });
            }}
            disabled={busyKey !== null || diagnosticQuery.trim().length < 2}
            className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-700 disabled:opacity-50"
          >
            Search
          </button>
        </div>
        <div className="mt-4 space-y-3">
          {diagnosticResults.map((result) => (
            <div key={`${result.accountKey}-${result.sourceSurface}-${result.resourceName}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700">
              <div className="flex items-center gap-2">
                <span className="rounded bg-white px-2 py-0.5 font-bold text-slate-700">{result.accountKey}</span>
                <span className={`rounded px-2 py-0.5 font-bold ${surfaceBadgeTone(result.sourceSurface)}`}>{result.sourceSurfaceLabel}</span>
                <span className="font-semibold text-slate-900">{result.primaryName || result.resourceName}</span>
              </div>
              <p className="mt-1 text-slate-500">{result.normalizedEmails.join(', ') || 'No email'} {result.normalizedPhoneKeys.length ? `· phones ${result.normalizedPhoneKeys.join(', ')}` : ''}</p>
              <p className="mt-1 text-slate-500">Auto-link blocked: {result.autoLinkBlockedReason || 'none'}</p>
              <p className="mt-1 text-slate-500">Linked customers: {result.linkedCustomers.map((customer: any) => customer.id).join(', ') || 'none'}</p>
              <p className="mt-1 text-slate-500">Candidate matches: {result.candidateCustomers.map((customer: any) => `${customer.id} (${customer.matchReason})`).join(', ') || 'none'}</p>
            </div>
          ))}
          {diagnosticResults.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
              Run a diagnostic search to inspect Google saved contacts and other contacts across all synced accounts.
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
        <div className="space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <h4 className="text-sm font-semibold text-slate-900">Duplicate Risk Review</h4>
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-amber-700">
              {activeRiskCount} active
            </span>
          </div>
          <div className="space-y-3">
            {state.risks.map(risk => (
              <div key={risk.id} className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-center gap-2">
                  <span className="rounded bg-white px-2 py-0.5 text-[10px] font-bold text-slate-700">{risk.accountKey}</span>
                  <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${surfaceBadgeTone(risk.sourceSurface)}`}>{surfaceLabel(risk.sourceSurface)}</span>
                  <span className="text-sm font-semibold text-slate-900">{risk.primaryName || 'Unnamed Google Contact'}</span>
                </div>
                <p className="mt-1 text-xs text-slate-600">
                  {(risk.emails || []).join(', ') || 'No email'} {risk.phones.length ? `· ${(risk.phones || []).join(', ')}` : ''}
                </p>
                <div className="mt-3 space-y-2">
                  {risk.customerIds.map(customerId => {
                    const customer = state.customersById.get(customerId);
                    return (
                      <div key={customerId} className="rounded-lg border border-white bg-white/80 px-3 py-2 text-xs text-slate-700">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-semibold">{customer?.name || customerId}</div>
                            <div className="text-slate-500">
                              {customer?.email || customer?.phoneNumber || 'No direct contact'} {customer?.company ? `· ${customer.company}` : ''} {customer?.status ? `· ${customer.status}` : ''}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setSelectedCandidate(customer || { id: customerId })}
                            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-700"
                          >
                            Open
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => void openRiskDetail(risk.id)}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-700"
                  >
                    View Google Source
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setBusyKey(`create-${risk.id}`);
                      void runAction('New LEAD created from Google source.', () => contactsApi.createCustomerFromGoogleSource(risk.id));
                    }}
                    className="rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-700"
                  >
                    Create New Customer
                  </button>
                  <button
                    type="button"
                    onClick={() => void openRiskDetail(risk.id).then(() => setIgnoreReason(''))}
                    className="rounded-lg border border-amber-200 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-amber-700"
                  >
                    Ignore
                  </button>
                </div>
              </div>
            ))}
            {!loading && state.risks.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
                No Google duplicate-risk contacts are currently recorded.
              </div>
            ) : null}
          </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="mb-4 flex items-center gap-2">
              <Eye className="h-4 w-4 text-slate-600" />
              <h4 className="text-sm font-semibold text-slate-900">Ignored Review Items</h4>
              <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-700">
                {ignoredRiskCount}
              </span>
            </div>
            <div className="space-y-3">
              {state.ignoredRisks.map(risk => (
                <div key={risk.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="rounded bg-white px-2 py-0.5 text-[10px] font-bold text-slate-700">{risk.accountKey}</span>
                        <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${surfaceBadgeTone(risk.sourceSurface)}`}>{surfaceLabel(risk.sourceSurface)}</span>
                        <span className="text-sm font-semibold text-slate-900">{risk.primaryName || 'Unnamed Google Contact'}</span>
                      </div>
                      <p className="mt-1 text-xs text-slate-600">
                        {(risk.emails || []).join(', ') || 'No email'} {risk.phones.length ? `· ${(risk.phones || []).join(', ')}` : ''}
                      </p>
                      <p className="mt-1 text-[11px] text-slate-500">Ignored {fmtDateTime(risk.ignoredAt)}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => void openRiskDetail(risk.id)}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-700"
                      >
                        View Source
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setBusyKey(`unignore-${risk.id}`);
                          void runAction('Duplicate risk returned to active review.', () => contactsApi.unignoreGoogleSource(risk.id));
                        }}
                        className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-blue-700"
                      >
                        Unignore
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {!loading && state.ignoredRisks.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
                  No ignored Google duplicate-risk contacts are currently parked.
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-2">
            <Link2 className="h-4 w-4 text-blue-600" />
            <h4 className="text-sm font-semibold text-slate-900">Recent Sync Runs</h4>
          </div>
          <div className="space-y-3">
            {state.runs.map(run => (
              <div key={run.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-2">
                  <span className="rounded bg-white px-2 py-0.5 text-[10px] font-bold text-slate-700">{run.accountKey}</span>
                  <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${surfaceBadgeTone(run.sourceSurface)}`}>{surfaceLabel(run.sourceSurface)}</span>
                  <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${run.status === 'SUCCESS' ? 'bg-emerald-100 text-emerald-700' : run.status === 'PARTIAL' ? 'bg-amber-100 text-amber-700' : run.status === 'FAILED' || run.status === 'CANCELLED' ? 'bg-rose-100 text-rose-700' : run.status === 'QUEUED' ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}`}>
                    {run.status}
                  </span>
                  {run.isDryRun ? <span className="rounded bg-slate-200 px-2 py-0.5 text-[10px] font-bold text-slate-700">DRY RUN</span> : null}
                </div>
                <p className="mt-2 text-xs text-slate-600">
                  {run.syncType} · pages {run.pagesProcessed || 0} · scanned {run.scannedCount} · create {run.createdCustomerCount} · link {run.linkedCustomerCount} · update {run.updatedCustomerCount} · duplicate risk {run.duplicateRiskCount} · skipped unmatched {run.skippedUnmatchedCount || 0}
                </p>
                <p className="mt-1 text-[11px] text-slate-500">
                  Started {fmtDateTime(run.startedAt)} · Heartbeat {fmtDateTime(run.heartbeatAt)} · Finished {fmtDateTime(run.finishedAt)}
                </p>
                {(run.currentPageToken || run.nextPageToken) ? (
                  <p className="mt-1 text-[11px] text-slate-500">
                    Checkpoint token {fmtToken(run.nextPageToken || run.currentPageToken)}
                  </p>
                ) : null}
                {run.status === 'QUEUED' || run.status === 'RUNNING' ? (
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
                    <div className="h-full w-1/2 animate-pulse rounded-full bg-sky-500" />
                  </div>
                ) : null}
                {run.errorMessage ? (
                  <p className="mt-2 text-xs text-rose-600">{run.errorMessage}</p>
                ) : null}
                {(run.sampleErrors?.length || 0) > 0 ? (
                  <div className="mt-3 rounded-lg border border-rose-100 bg-white p-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-rose-500">Sample Errors</p>
                    <div className="mt-2 space-y-1 text-xs text-slate-700">
                      {run.sampleErrors?.map((item, index) => (
                        <div key={`${run.id}-error-${index}`}>
                          <span className="font-semibold">{item.externalId}</span>: {item.message}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                {(run.sampleDuplicateRisks?.length || 0) > 0 ? (
                  <div className="mt-3 rounded-lg border border-amber-100 bg-white p-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber-600">Duplicate Examples</p>
                    <div className="mt-2 space-y-1 text-xs text-slate-700">
                      {run.sampleDuplicateRisks?.map((item, index) => (
                        <div key={`${run.id}-dup-${index}`}>
                          <span className="font-semibold">{item.primaryName || item.externalId}</span>
                          {item.emails.length ? ` · ${item.emails.join(', ')}` : ''}
                          {item.customerIds.length ? ` · matches ${item.customerIds.join(', ')}` : ''}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
            {!loading && state.runs.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
                No Google sync runs recorded yet.
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {selectedRiskDetail ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 px-4" onClick={() => setSelectedRiskDetail(null)}>
          <div className="max-h-[85vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Google Source Detail</h3>
                <p className="mt-1 text-xs text-slate-500">{selectedRiskDetail.fields.primaryName || 'Unnamed Google Contact'} · {selectedRiskDetail.source?.externalId || '—'} · {surfaceLabel((selectedRiskDetail.fields.sourceSurface || selectedRiskDetail.source?.sourceSurface || 'CONTACT') as 'CONTACT' | 'OTHER_CONTACT')}</p>
              </div>
              <button onClick={() => setSelectedRiskDetail(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-5 overflow-y-auto px-5 py-4 lg:grid-cols-[minmax(0,1fr)_320px]">
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-700">
                    <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Emails</p>
                    <p className="mt-1">{selectedRiskDetail.fields.emails.join(', ') || '—'}</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-700">
                    <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Phones</p>
                    <p className="mt-1">{selectedRiskDetail.fields.phones.join(', ') || '—'}</p>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Match Diagnostics</p>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-700">
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Normalized Emails</p>
                      <p className="mt-1">{(selectedRiskDetail.fields.normalizedEmails || []).join(', ') || '—'}</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-700">
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Normalized Phone Keys</p>
                      <p className="mt-1">{(selectedRiskDetail.fields.normalizedPhoneKeys || []).join(', ') || '—'}</p>
                    </div>
                  </div>
                  <div className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-amber-700">Auto-link Block</p>
                    <p className="mt-1">{selectedRiskDetail.autoLinkBlockedReason || selectedRiskDetail.fields.autoLinkBlockedReason || 'No block reason recorded'}</p>
                  </div>
                </div>
                {selectedRiskDetail.source?.ignoredAt ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                    <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Ignored State</p>
                    <p className="mt-1">Ignored {fmtDateTime(selectedRiskDetail.source.ignoredAt)}</p>
                    {selectedRiskDetail.source?.ignoredReason ? (
                      <p className="mt-1 text-slate-500">{selectedRiskDetail.source.ignoredReason}</p>
                    ) : null}
                  </div>
                ) : null}
                <div className="rounded-xl border border-slate-200 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Candidates</p>
                  <div className="mt-3 space-y-2">
                    {selectedRiskDetail.candidates.map((customer: any) => (
                      <div key={customer.id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-xs text-slate-700">
                            <div className="font-semibold">{customer.name}</div>
                            <div className="text-slate-500">{customer.email || customer.phoneNumber || 'No direct contact'} {customer.company ? `· ${customer.company}` : ''} {customer.status ? `· ${customer.status}` : ''}</div>
                            {(() => {
                              const diag = (selectedRiskDetail.candidateDiagnostics || []).find(item => item.customerId === customer.id);
                              if (!diag) return null;
                              return (
                                <div className="mt-1 text-[11px] text-slate-500">
                                  Match: {diag.matchedOnEmail || '—'} / {diag.matchedOnPhone || '—'}
                                  {diag.normalizedCustomerEmail ? ` · email ${diag.normalizedCustomerEmail}` : ''}
                                  {diag.normalizedCustomerPhoneKey ? ` · phone ${diag.normalizedCustomerPhoneKey}` : ''}
                                </div>
                              );
                            })()}
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setSelectedCandidate(customer)}
                              className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-700"
                            >
                              <Eye className="mr-1 inline h-3 w-3" />
                              Open
                            </button>
                            <button
                              type="button"
                              onClick={() => setLinkTarget({ sourceId: selectedRiskDetail.source.id, customer })}
                              className="rounded-lg border border-blue-200 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-blue-700"
                            >
                              <Link2 className="mr-1 inline h-3 w-3" />
                              Link
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {selectedRiskDetail.candidates.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-xs text-slate-500">
                        No candidate customers recorded for this source.
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Snapshot</p>
                  <pre className="mt-2 max-h-[260px] overflow-auto whitespace-pre-wrap break-words text-[11px] text-slate-700">
                    {JSON.stringify(selectedRiskDetail.fields.rawSnapshot || {}, null, 2)}
                  </pre>
                </div>
              </div>
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => {
                    setBusyKey(`create-detail-${selectedRiskDetail.source.id}`);
                    void runAction('New LEAD created from Google source.', () => contactsApi.createCustomerFromGoogleSource(selectedRiskDetail.source.id));
                    setSelectedRiskDetail(null);
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-200 px-4 py-3 text-xs font-bold uppercase tracking-[0.16em] text-emerald-700"
                >
                  <Plus className="h-4 w-4" />
                  Create New Customer
                </button>
                {selectedRiskDetail.source?.ignoredAt ? (
                  <button
                    type="button"
                    onClick={() => {
                      const sourceId = selectedRiskDetail.source.id;
                      setBusyKey(`unignore-detail-${sourceId}`);
                      void runAction('Duplicate risk returned to active review.', () => contactsApi.unignoreGoogleSource(sourceId));
                      setSelectedRiskDetail(null);
                    }}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-blue-200 px-4 py-3 text-xs font-bold uppercase tracking-[0.16em] text-blue-700"
                  >
                    Return To Review
                  </button>
                ) : null}
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber-700">Ignore Duplicate Risk</p>
                  <textarea
                    value={ignoreReason}
                    onChange={(e) => setIgnoreReason(e.target.value)}
                    placeholder="Optional reason"
                    className="mt-2 min-h-[88px] w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs text-slate-700"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const sourceId = selectedRiskDetail.source.id;
                      setBusyKey(`ignore-${sourceId}`);
                      void runAction('Duplicate risk ignored.', () => contactsApi.ignoreGoogleSource(sourceId, { reason: ignoreReason || null }));
                      setSelectedRiskDetail(null);
                      setIgnoreReason('');
                    }}
                    className="mt-3 w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-amber-700"
                  >
                    Mark Ignored
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {selectedCandidate ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 px-4" onClick={() => setSelectedCandidate(null)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Candidate Customer</h3>
                <p className="mt-1 text-xs text-slate-500">{selectedCandidate.id}</p>
              </div>
              <button onClick={() => setSelectedCandidate(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3 text-xs text-slate-700">
              <div className="rounded-xl bg-slate-50 p-3"><span className="font-bold">Name:</span> {selectedCandidate.name || '—'}</div>
              <div className="rounded-xl bg-slate-50 p-3"><span className="font-bold">Email:</span> {selectedCandidate.email || '—'}</div>
              <div className="rounded-xl bg-slate-50 p-3"><span className="font-bold">Phone:</span> {selectedCandidate.phoneNumber || '—'}</div>
              <div className="rounded-xl bg-slate-50 p-3"><span className="font-bold">Company:</span> {selectedCandidate.company || '—'}</div>
              <div className="rounded-xl bg-slate-50 p-3"><span className="font-bold">Status:</span> {selectedCandidate.status || '—'}</div>
            </div>
          </div>
        </div>
      ) : null}

      {linkTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 px-4" onClick={() => setLinkTarget(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-slate-900">Confirm Link</h3>
            <p className="mt-2 text-sm text-slate-600">
              Link this Google source to <span className="font-semibold">{linkTarget.customer.name || linkTarget.customer.id}</span>?
            </p>
            <p className="mt-1 text-xs text-slate-500">
              This only creates a Google external identity link and missing contact methods. It does not merge customers or write back to Google.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setLinkTarget(null)} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">
                Cancel
              </button>
              <button
                onClick={() => {
                  const target = linkTarget;
                  setBusyKey(`link-${target.sourceId}-${target.customer.id}`);
                  void runAction('Google source linked to customer.', () => contactsApi.linkGoogleSourceToCustomer(target.sourceId, target.customer.id));
                  setLinkTarget(null);
                  setSelectedRiskDetail(null);
                }}
                className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold uppercase tracking-[0.14em] text-white"
              >
                Confirm Link
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
